import { BALANCE } from '../balance.ts';
import { getMap, hasRoom } from '../maps/graph.ts';
import { validatePhrase } from '../phrases.ts';
import { err, ok, type Result } from '../result.ts';
import type {
  Action,
  ActionEnvelope,
  ActionError,
  ApplyOutput,
  GameEvent,
  GameState,
  PlayerId,
} from '../types.ts';
import { emit, fail, playerOf, type Ctx } from './context.ts';
import { setPhase } from './core.ts';
import { execStep, puzzleAct, skipOptional, useGear, usePower } from './execution.ts';
import {
  accuse,
  endGame,
  placePin,
  proposeSplit,
  tickHideout,
  tickReveal,
  voteSplit,
} from './hideout.ts';
import { useSabotage } from './infiltrator.ts';
import { propose, tickPlanning, tiebreak, vote } from './planning.ts';

function startPlanning(ctx: Ctx): void {
  setPhase(ctx, 'planning');
  ctx.s.planning.stageStartedMs = ctx.s.clockMs;
}

function stepClock(ctx: Ctx, ms: number): void {
  const s = ctx.s;
  s.clockMs += ms;
  switch (s.phase) {
    case 'briefing':
      if (s.clockMs - s.phaseStartedMs >= BALANCE.briefingMs) startPlanning(ctx);
      break;
    case 'planning':
      tickPlanning(ctx);
      break;
    case 'execution':
    case 'escape':
      execStep(ctx, ms);
      break;
    case 'hideout':
      tickHideout(ctx);
      break;
    case 'reveal':
      tickReveal(ctx);
      break;
    case 'ended':
      break;
  }
}

function tick(ctx: Ctx, dtMs: number): ActionError | null {
  if (!Number.isInteger(dtMs) || dtMs < 1 || dtMs > BALANCE.maxTickMs) {
    return fail('invalid', `a tick is 1–${BALANCE.maxTickMs} ms`);
  }
  // Long ticks are processed in small steps so timed things happen in the right order.
  let left = dtMs;
  while (left > 0 && ctx.s.phase !== 'ended') {
    const step = Math.min(left, BALANCE.tickStepMs);
    stepClock(ctx, step);
    left -= step;
  }
  return null;
}

function dispatch(ctx: Ctx, by: PlayerId, action: Action): ActionError | null {
  const s = ctx.s;
  switch (action.type) {
    case 'ready': {
      if (s.phase !== 'briefing') return fail('wrongPhase', 'the briefing is over');
      if (!s.ready.includes(by)) s.ready.push(by);
      emit(ctx, 'ready', { by });
      if (s.ready.length >= s.players.length) startPlanning(ctx);
      return null;
    }
    case 'plan.propose':
      return s.phase === 'planning'
        ? propose(ctx, by, action.plan)
        : fail('wrongPhase', 'not planning');
    case 'plan.vote':
      return s.phase === 'planning'
        ? vote(ctx, by, action.approve)
        : fail('wrongPhase', 'not planning');
    case 'plan.tiebreak':
      return s.phase === 'planning'
        ? tiebreak(ctx, by, action.decision)
        : fail('wrongPhase', 'not planning');
    case 'puzzle.act':
      return puzzleAct(ctx, by, action.puzzleId, action.act);
    case 'gear.use':
      return useGear(ctx, by, action.gear, action.puzzleId);
    case 'power.use':
      return usePower(ctx, by, action.power);
    case 'exec.skipOptional':
      return skipOptional(ctx, by);
    case 'sabotage.use':
      return useSabotage(ctx, by, action.kind);
    case 'chat.quick': {
      const problem = validatePhrase(action.phrase);
      if (problem) return fail('invalid', problem);
      emit(ctx, 'chat', { by, phrase: action.phrase });
      return null;
    }
    case 'ping': {
      if (action.room !== null && !hasRoom(getMap(s.config.mapId), action.room)) {
        return fail('invalid', 'no such room');
      }
      emit(ctx, 'ping', { by, kind: action.kind, room: action.room });
      return null;
    }
    case 'pin.place':
      return placePin(ctx, by, action.target);
    case 'accuse.vote':
      return accuse(ctx, by, action.target);
    case 'split.propose':
      return proposeSplit(ctx, by, action.shares);
    case 'split.vote':
      return voteSplit(ctx, by, action.approve);
    case 'reveal.continue': {
      if (s.phase !== 'reveal') return fail('wrongPhase', 'nothing to continue');
      endGame(ctx);
      return null;
    }
    case 'tick':
      return fail('notAllowed', 'only the server ticks');
  }
}

/**
 * Applies one action to `state` *in place* and returns the events it produced. For callers
 * that own the state (the server, the simulator), where copying a state per action would be
 * wasteful. If the action fails the state is left exactly as it was.
 */
export function applyActionInPlace(
  state: GameState,
  env: ActionEnvelope,
): Result<GameEvent[], ActionError> {
  if (state.phase === 'ended') return err(fail('gameOver', 'the game is over'));
  const action = env.action;

  // A valid tick cannot fail half-way (the length is checked before anything changes), so it
  // runs straight on the state: a tick arrives every second and a copy of the log would add up.
  if (
    action.type === 'tick' &&
    env.by === 'server' &&
    Number.isInteger(action.dtMs) &&
    action.dtMs >= 1 &&
    action.dtMs <= BALANCE.maxTickMs
  ) {
    const ctx: Ctx = { s: state, events: [] };
    tick(ctx, action.dtMs);
    state.nextActionSeq += 1;
    return ok(ctx.events);
  }

  // Failed actions must not change anything: work on a copy and swap only on success.
  const draft = structuredClone(state);
  const ctx: Ctx = { s: draft, events: [] };

  let error: ActionError | null;
  if (action.type === 'tick') {
    error =
      env.by === 'server' ? tick(ctx, action.dtMs) : fail('notAllowed', 'only the server ticks');
  } else if (env.by === 'server') {
    error = fail('notAllowed', 'the server cannot act as a player');
  } else if (!playerOf(draft, env.by)) {
    error = fail('notAllowed', 'you are not in this game');
  } else {
    error = dispatch(ctx, env.by, action);
  }
  if (error) return err(error);

  draft.nextActionSeq += 1;
  // Replace the contents of `state` with the successful draft.
  for (const key of Object.keys(state) as (keyof GameState)[])
    delete (state as Partial<GameState>)[key];
  Object.assign(state, draft);
  return ok(ctx.events);
}

/** Pure version: returns a new state and the events, or an error. The input is untouched. */
export function applyAction(
  state: GameState,
  env: ActionEnvelope,
): Result<ApplyOutput, ActionError> {
  const next = structuredClone(state);
  const r = applyActionInPlace(next, env);
  return r.ok ? ok({ state: next, events: r.value }) : r;
}
